import { useState, useEffect } from "react";

/**
 * Detects if the device is a mobile or tablet based on User Agent
 * Includes special handling for iPads (both old and new M1/M2 Macs)
 * Note: This check is performed once on mount and does not update on resize
 *
 * @returns {boolean} True if device is mobile or tablet hardware
 */
function isMobileOrTabletDevice() {
  if (typeof navigator === "undefined" || typeof document === "undefined") {
    return false;
  }

  const ua = navigator.userAgent.toLowerCase();
  const basicCheck =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet|samsungbrowser/i.test(
      ua,
    );
  const isOldIPad = /ipad/.test(ua);
  const isNewIPad = /macintosh/.test(ua) && "ontouchstart" in document;
  return basicCheck || isOldIPad || isNewIPad;
}

/**
 * Gets the initial screen size state
 *
 * @returns {object} Initial screen size state
 */
function getInitialScreenSize() {
  if (typeof window === "undefined") {
    return {
      isMobile: false,
      isTabletSize: false,
      isDesktop: true,
      isLargerDesktop: true,
    };
  }

  const width = window.innerWidth;
  return {
    isMobile: width < 768,
    isTabletSize: width >= 768 && width < 1024,
    isDesktop: width >= 1024,
    isLargerDesktop: width >= 1440,
  };
}

/**
 * Provides responsive breakpoints and hardware device detection
 *
 * Breakpoints:
 * - Mobile: < 768px
 * - Tablet: 768px - 1023px
 * - Desktop: >= 1024px
 * - LargerDesktop: >= 1440px
 *
 * @returns {object} Screen size and device detection object
 * @returns {boolean} returns.isWebScreen - True if NOT a mobile/tablet device (hardware check, does not update on resize)
 * @returns {boolean} returns.isMobile - Screen width < 768px
 * @returns {boolean} returns.isTabletSize - Screen width between 768px and 1023px
 * @returns {boolean} returns.isDesktop - Screen width >= 1024px
 * @returns {boolean} returns.isLargerDesktop - Screen width >= 1440px
 *
 */
const useScreenSize = () => {
  // Device detection happens once - device type doesn't change during session
  const [isWebScreen] = useState(() =>
    typeof window !== "undefined" ? !isMobileOrTabletDevice() : true,
  );

  const [screenSize, setScreenSize] = useState(getInitialScreenSize);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize({
        isMobile: width < 768,
        isTabletSize: width >= 768 && width < 1024,
        isDesktop: width >= 1024,
        isLargerDesktop: width >= 1440,
      });
    };

    // Set correct values on mount
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return {
    isWebScreen,
    ...screenSize,
  };
};

export default useScreenSize;
